import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ActiviteEntite } from '../entites/activite';

export interface TimelineConfig {
  title?: string;
  description?: string;
  showReloadButton?: boolean;
  isLoading?: boolean;
  emptyMessage?: string;
}

@Component({
  selector: 'app-timeline-activite',
  templateUrl: './timeline-activite.component.html',
  styleUrls: ['./timeline-activite.component.scss']
})
export class TimelineActiviteComponent implements OnInit {
  @Input() activites: ActiviteEntite[] = [];
  @Input() config: TimelineConfig = {};
  @Input() isLoading: boolean = false;
  
  @Output() onReload = new EventEmitter<void>();
  @Output() onNavigate = new EventEmitter<string>();

  // Default configuration
  defaultConfig: TimelineConfig = {
    title: 'Historique des activités',
    description: 'Chronologie de toutes les actions effectuées',
    showReloadButton: true,
    emptyMessage: 'Aucune activité trouvée'
  };

  constructor(private router: Router) {}

  ngOnInit() {
    // Merge default config with provided config
    this.config = { ...this.defaultConfig, ...this.config };
  }

  /**
   * Handles reload button click
   */
  onReloadClick() {
    this.onReload.emit();
  }

  /**
   * Handles navigation to router link
   */
  onNavigateClick(routerLink: string) {
    if (routerLink) {
      this.onNavigate.emit(routerLink);
      this.router.navigate([routerLink]);
    }
  }

  /**
   * Gets the icon for the timeline item based on the activity action
   */
  getTimelineIcon(activite: ActiviteEntite): string {
    if (!activite.titre) return 'pi-circle';
    
    const title = activite.titre.toLowerCase();
    if (title.includes('créer') || title.includes('ajouter')) return 'pi-plus';
    if (title.includes('modifier') || title.includes('mettre à jour')) return 'pi-pencil';
    if (title.includes('supprimer') || title.includes('effacer')) return 'pi-trash';
    if (title.includes('approuver') || title.includes('valider')) return 'pi-check';
    if (title.includes('envoyer') || title.includes('transmettre')) return 'pi-send';
    if (title.includes('remplacer') || title.includes('actualiser')) return 'pi-refresh';
    if (title.includes('convertir') || title.includes('transformer')) return 'pi-arrow-right';
    if (title.includes('consulter') || title.includes('voir')) return 'pi-eye';
    if (title.includes('télécharger') || title.includes('download')) return 'pi-download';
    if (title.includes('importer') || title.includes('import')) return 'pi-upload';
    
    return 'pi-circle';
  }

  /**
   * Gets the color for the timeline item based on the activity action
   */
  getTimelineColor(activite: ActiviteEntite): string {
    if (!activite.titre) return '#6c757d';
    
    const title = activite.titre.toLowerCase();
    if (title.includes('créer') || title.includes('ajouter')) return '#28a745'; // Green
    if (title.includes('modifier') || title.includes('mettre à jour')) return '#ffc107'; // Yellow
    if (title.includes('supprimer') || title.includes('effacer')) return '#dc3545'; // Red
    if (title.includes('approuver') || title.includes('valider')) return '#007bff'; // Blue
    if (title.includes('envoyer') || title.includes('transmettre')) return '#17a2b8'; // Cyan
    if (title.includes('remplacer') || title.includes('actualiser')) return '#fd7e14'; // Orange
    if (title.includes('convertir') || title.includes('transformer')) return '#6f42c1'; // Purple
    if (title.includes('consulter') || title.includes('voir')) return '#20c997'; // Teal
    if (title.includes('télécharger') || title.includes('download')) return '#6610f2'; // Indigo
    if (title.includes('importer') || title.includes('import')) return '#e83e8c'; // Pink
    
    return '#6c757d'; // Gray default
  }

  /**
   * Formats the activity date for display with minutes precision
   */
  formatActivityDate(date: Date): string {
    if (!date) return '';
    return new Date(date).toLocaleString('fr-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Check if activity has a valid router link
   */
  hasRouterLink(activite: ActiviteEntite): boolean {
    return !!(activite.routerLink && activite.routerLink.trim());
  }
}